import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LibrarySourceCoverPlaceholder } from "@/components/library/LibrarySourceCoverPlaceholder";
import { OpenAccessLink } from "@/components/library/OpenAccessLink";
import {
  deriveLibraryTitle,
  getLibraryProviderLabel,
  getLibrarySourceTypeLabel,
  type LibrarySearchResult,
} from "@/lib/library";

type Props = {
  query: string;
  onQueryChange: (q: string) => void;
  searching: boolean;
  results: LibrarySearchResult[];
  onSearch: () => void;
  onPick: (r: LibrarySearchResult) => void;
};

export const AddSourceSearchPanel = ({
  query,
  onQueryChange,
  searching,
  results,
  onSearch,
  onPick,
}: Props) => (
  <>
    <div className="space-y-2">
      <Label htmlFor="add-source-search">Search by title, author, ISBN, or DOI</Label>
      <div className="flex gap-2">
        <Input
          id="add-source-search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSearch();
            }
          }}
          placeholder="e.g. Marcus Aurelius Meditations…"
        />
        <Button type="button" onClick={onSearch} disabled={searching}>
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          <span className="ml-2">Search</span>
        </Button>
      </div>
    </div>

    {results.length > 0 && (
      <div className="space-y-2 rounded-md border">
        {results.map((result, idx) => {
          const c = result.completeness;
          const badges: Array<{ label: string; ok: boolean }> = [
            { label: "ISBN", ok: c.hasIsbn },
            { label: "Cover", ok: c.hasCover },
            { label: "Publisher", ok: c.hasPublisher },
            { label: "Pages", ok: c.hasPageCount },
            { label: "Description", ok: c.hasDescription },
          ];
          return (
            <button
              key={`${result.sourceProvider}-${result.sourceId ?? idx}`}
              type="button"
              onClick={() => onPick(result)}
              className="flex w-full items-start gap-3 border-b px-3 py-3 text-left text-sm last:border-b-0 hover:bg-accent"
            >
              {result.coverUrl ? (
                <img src={result.coverUrl} alt="" loading="lazy" decoding="async" className="h-20 w-14 shrink-0 rounded object-cover" />
              ) : (
                <div className="h-20 w-14 shrink-0 overflow-hidden rounded">
                  <LibrarySourceCoverPlaceholder sourceType={result.sourceType ?? "other"} size="sm" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-medium">{deriveLibraryTitle({ title: result.title, subtitle: result.subtitle, display_title: result.displayTitle })}</div>
                {result.authorText && (
                  <div className="text-xs text-muted-foreground">{result.authorText}</div>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                  <span className="rounded bg-primary/10 px-1.5 py-0.5 font-medium text-primary">
                    {getLibraryProviderLabel(result.sourceProvider)}
                  </span>
                  <span className="uppercase tracking-wide text-muted-foreground">
                    {getLibrarySourceTypeLabel(result.sourceType)}
                    {result.publishedYear ? ` · ${result.publishedYear}` : ""}
                  </span>
                  {badges.map((b) => (
                    <span
                      key={b.label}
                      className={
                        b.ok
                          ? "rounded bg-emerald-500/10 px-1.5 py-0.5 text-emerald-700 dark:text-emerald-300"
                          : "rounded bg-muted px-1.5 py-0.5 text-muted-foreground line-through opacity-60"
                      }
                    >
                      {b.ok ? "✓" : "·"} {b.label}
                    </span>
                  ))}
                  {result.openAccessUrl ? <OpenAccessLink href={result.openAccessUrl} /> : null}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    )}

    <p className="text-xs text-muted-foreground">
      Pick a result to prefill the manual editor, or switch to the Manual tab to start from a blank slate.
    </p>
  </>
);
